import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    DialogActions,
    CircularProgress,
} from '@mui/material';
import Layout from './Layout';
import axiosInstance from '../axiosConfig';

const IncidentManagementPage = () => {
    const { surveyId } = useParams();
    const [loading, setLoading] = useState(false);
    const [survey, setSurvey] = useState(null);
    const [incidents, setIncidents] = useState([]);
    const [loginOpen, setLoginOpen] = useState(true);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [loginError, setLoginError] = useState('');

    useEffect(() => {
        const token = localStorage.getItem('authToken');
        if (token) {
            validateToken(token);
        } else {
            setLoginOpen(true); // Open login if no token
        }
    }, []);

    const validateToken = async (token) => {
        setLoading(true);
        try {
            const response = await axiosInstance.post('/api/reports/validate-token', { token });
            if (response.status === 200) {
                fetchSurveyDetails();
            } else {
                setLoginOpen(true); // Open login if token is invalid
            }
        } catch (error) {
            console.error('Token validation failed:', error);
            setLoginOpen(true); // Open login if validation fails
        } finally {
            setLoading(false);
        }
    };

    const fetchSurveyDetails = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get(`/api/surveys/${surveyId}`);
            setSurvey(response.data);

            const incidentsResponse = await axiosInstance.get(`/api/surveys/${surveyId}/incidents`);
            setIncidents(incidentsResponse.data);
        } catch (error) {
            console.error('Error fetching survey or incidents:', error);
        } finally {
            setLoading(false);
        }
    };


    const handleLogin = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.post('/api/reports/validate', {
                surveyId,
                username,
                password,
            });

            if (response.status === 200) {
                const { token } = response.data;
                localStorage.setItem('authToken', token); // Save token
                setLoginOpen(false); // Close login dialog
                fetchSurveyDetails(); // Fetch data after login
            }
        } catch (error) {
            setLoginError('Invalid credentials or network issue.');
        } finally {
            setLoading(false);
        }
    };
    

    return (
        <Layout>
            <Dialog open={loginOpen} disableEscapeKeyDown>
                <DialogTitle>Login</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Username"
                        fullWidth
                        margin="normal"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                    />
                    <TextField
                        label="Password"
                        type="password"
                        fullWidth
                        margin="normal"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                    />
                    {loginError && (
                        <Typography color="error" variant="body2">
                            {loginError}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleLogin} variant="contained" color="primary" disabled={loading}>
                        {loading ? <CircularProgress size={24} /> : 'Login'}
                    </Button>
                </DialogActions>
            </Dialog>
            {!loginOpen && (
                <Box sx={{ padding: '20px', backgroundColor: '#f9f9f9', minHeight: '100vh' }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', marginBottom: '20px', color: '#1e2742' }}>
                        Incident Management for: {survey?.surveyName || 'Loading...'}
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        sx={{ backgroundColor: '#bc2534', '&:hover': { backgroundColor: '#9b1f2d' }, marginBottom: '20px' }}
                        onClick={() => console.log(`Create Incident for Survey ID: ${surveyId}`)}
                    >
                        Create New Incident
                    </Button>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                            <CircularProgress />
                        </Box>
                    ) : incidents.length > 0 ? (
                        incidents.map((incident, index) => (
                            <Box key={index} sx={{ marginBottom: '10px', border: '1px solid #ddd', padding: '10px', borderRadius: '8px' }}>
                                <Typography variant="h6">{incident.title}</Typography>
                                <Typography variant="body2">{incident.description}</Typography>
                            </Box>
                        ))
                    ) : (
                        <Typography variant="body1" color="textSecondary">
                            No incidents to display. Create a new incident to get started!
                        </Typography>
                    )}
                </Box>
            )}
        </Layout>
    );
};

export default IncidentManagementPage;
